import { useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import jwt_decode from 'jwt-decode';
import styled from 'styled-components';

import useMutationHook from '@/hooks/useMutationHook';
import { useSocket } from '@/hooks/useSocket';
import useModal from '@/hooks/useModal';
import AgreeTerms from '@/containers/modal/login/AgreeTerms';
import RegistGuest from '@/queries/common/registGuest';
import { loginState, dummyUserState, isAppState, groupProposalState, isLoadingState } from '@/recoil';
import { API, URLs } from '@/api';
import { FontStyle } from '@/utils/StyleUtil';
import { IJwt } from '@/models';

interface IOAuth {
    platform: string;
    //oAuthIdToken: string;
    //platformId: string;
    //email: string;
    //isApp: boolean;
    code: string;
    friendCode?: string;
}

const PlatformLogin = () => {
    const router = useRouter();
    const { registGuest } = RegistGuest();
    const { addModal, removeCurrentModal, clearModalList } = useModal();
    const { connectSocket, disconnectSocket } = useSocket();

    const isApp = useRecoilValue(isAppState);
    const [loginStatus, setLoginStatus] = useRecoilState(loginState);
    const [dummyUser, setDummyUser] = useRecoilState(dummyUserState);
    const setIsLoading = useSetRecoilState(isLoadingState);
    const setIsGetGroupProposal = useSetRecoilState(groupProposalState);

    const getOAuthItems = () => {
        if (typeof window !== 'undefined') {
            if (navigator.userAgent.includes('Mac')) return ['google', 'kakao', 'naver', 'apple'];
            else return ['google', 'kakao', 'naver'];
        }
        return [];
    };

    const registeredUser = async (loginRes: any) => {
        if (window.ReactNativeWebView)
            window.ReactNativeWebView.postMessage(JSON.stringify({ clearAllPushNoti: true }));

        disconnectSocket();
        connectSocket(loginRes.tokenSet.accessToken, loginRes.tokenSet.refreshToken);

        setLoginStatus(prev => ({
            userType: 'client',
            clientId: loginRes.clientId,
            name: loginRes.clientName,
            email: loginRes.email,
            designatedPlannerName: loginRes.recommendedPlannerName,
            designatedPlannerId: loginRes.designatedPlannerId,
            abType: loginRes.abType ?? prev.abType,
        }));
        if (loginRes.groupProposalSubscribe) setIsGetGroupProposal(loginRes.groupProposalSubscribe);

        const { isBanned } = jwt_decode(loginRes.tokenSet.accessToken) as IJwt;
        if (router.pathname !== '/ban' && isBanned) router.push('/ban');

        clearModalList();
    };

    const unRegisteredUser = (loginRes: any) => {
        setDummyUser({
            ...dummyUser,
            clientId: loginStatus.clientId,
            platform: loginRes.platform,
            oAuthIdToken: loginRes.oAuthResult,
            platformId: loginRes?.platformId,
            email: loginRes?.email,
            friendCode: loginRes?.friendCode,
        });

        addModal({
            key: 'fullScreen',
            props: {
                content: <AgreeTerms />,
                handleCloseModal: removeCurrentModal,
                isCloseIconOnRight: false,
            },
        });
    };

    const isTokenExpired = async (item: string) => {
        const refreshToken = localStorage.getItem('refreshToken');
        const decoded = jwt_decode(refreshToken!) as IJwt;

        const clickPlatform = () => {
            // 친구초대 진입(/?friendCode=...) 시 코드를 로그인 체인에 전달
            const { friendCode } = router.query;
            const query = `platform=${item}${
                typeof friendCode === 'string' && friendCode ? `&friendCode=${friendCode}` : ''
            }`;
            if (isApp) {
                // 발급 시각을 함께 저장 — 소비 측(signIn)이 TTL로 잔존 코드를 걸러 비초대 가입에 끼는 것 방지
                if (typeof friendCode === 'string' && friendCode)
                    localStorage.setItem('friend-code', JSON.stringify({ code: friendCode, ts: Date.now() }));
                else localStorage.removeItem('friend-code');
                window.ReactNativeWebView.postMessage(JSON.stringify({ openLoginModal: item }));
            } else window.open(`${process.env.NEXT_PUBLIC_URL}/login/signIn?${query}`);
        };

        if (decoded.exp * 1000 < new Date().getTime())
            registGuest({
                onSuccess: clickPlatform,
            });
        else clickPlatform();
    };

    const { mutate: tryLogin, errorPopUp } = useMutationHook(
        ['try-login'],
        async ({ platform, code, friendCode }: IOAuth) => {
            const res = await API.post(URLs.auth.loginByNewOauth(platform), { oAuthResult: code });

            return { loginResult: res, platform: platform, code: code, friendCode: friendCode };
        },
        {
            onMutate: () => setIsLoading(false),
            onSuccess: async res => {
                const loginResult = res.loginResult;
                const platform = res.platform;
                const code = res.code;
                if (loginResult.isRegistered) {
                    const loginRes = {
                        clientId: loginResult.clientId,
                        platform: platform,
                        tokenSet: loginResult.tokenSet,
                        isRegistered: loginResult.isRegistered,
                        clientName: loginResult.clientName,
                    };
                    registeredUser(loginRes);
                    if (res.friendCode) {
                        addModal({
                            key: 'popUp',
                            props: {
                                type: 'confirm',
                                content: '이미 가입된 회원이에요\n친구 초대 이벤트는 신규회원만 해당됩니다',
                                handleCloseModal: removeCurrentModal,
                                onClickConfirm: removeCurrentModal,
                            },
                        });
                    }
                    if (router.asPath === '/myPlan/interest-detail') router.push('/myPlan');
                } else {
                    const loginRes = {
                        platform: platform,
                        oAuthResult: code,
                        isRegistered: loginResult.isRegistered,
                        friendCode: res.friendCode,
                    };
                    unRegisteredUser(loginRes);
                }
            },
            onError: err => {
                errorPopUp(err?.response?.status);
            },
        },
    );

    useEffect(() => {
        function handleLoginChange() {
            if (localStorage.getItem('login-status') === 'success' && localStorage.getItem('login-info')) {
                const loginInfo = JSON.parse(localStorage.getItem('login-info')!);
                localStorage.removeItem('login-status');
                localStorage.removeItem('login-info');
                tryLogin({ platform: loginInfo.platform, code: loginInfo.code, friendCode: loginInfo.friendCode });
            } else {
                localStorage.removeItem('login-status');
                localStorage.removeItem('login-info');
            }
        }

        window.addEventListener('storage', handleLoginChange);
        return () => window.removeEventListener('storage', handleLoginChange);
    }, []);

    useEffect(() => {
        if (window.ReactNativeWebView && localStorage.getItem('app-login-status') === 'success') {
            const loginInfo = JSON.parse(localStorage.getItem('login-info')!);
            localStorage.removeItem('login-status');
            localStorage.removeItem('login-info');
            localStorage.removeItem('app-login-status');
            tryLogin({ platform: loginInfo.platform, code: loginInfo.code, friendCode: loginInfo.friendCode });
        } else {
            localStorage.removeItem('login-status');
            localStorage.removeItem('login-info');
            localStorage.removeItem('app-login-status');
        }
    }, []);

    return (
        <Container>
            <LoginContainer>
                <div className="guide_text_box">
                    <div className="divider" />
                    <span className="guide_text">간편 로그인으로 시작하기</span>
                    <div className="divider" />
                </div>
                <AuthImagesContainer>
                    {getOAuthItems().map((item, index) => {
                        return (
                            <ImageBox key={index} index={index} onClick={() => isTokenExpired(item)}>
                                <Image src={`/img/auth/${item}@3x.png`} alt={item} width="56px" height="56px" />
                            </ImageBox>
                        );
                    })}
                </AuthImagesContainer>
            </LoginContainer>
        </Container>
    );
};

const Container = styled.div`
    margin-top: 20px;
    ${({ theme }) => theme.flex.flexColumn};
    ${({ theme }) => theme.flex.justifyCenter};
`;

const LoginContainer = styled.div`
    .guide_text_box {
        ${({ theme }) => theme.flex.flexOnly};
        ${({ theme }) => theme.flex.justifyCenter};
        margin-bottom: 18px;

        .divider {
            ${({ theme }) => theme.flex.flexOnly};
            flex: 1;
            height: 1px;
            background-color: #f1f1f1;
        }
        .guide_text {
            margin: 0 8px;
            ${FontStyle(12, 20, '400', '#acacac', -1)};
        }
    }
`;

const AuthImagesContainer = styled.div`
    ${({ theme }) => theme.flex.flexOnly};
`;

const ImageBox = styled.div<{ index: number }>`
    margin-left: ${({ index }) => (index !== 0 ? '15px' : undefined)};
`;

export default PlatformLogin;
